import { animate, stagger, inView } from 'motion'
import ease from '@/utils/ease'

export default () => ({
	quantity: 1,
	selectedOptions: {},
	variants: [],
	currentVariant: null,

	init() {
		this.animateLines()
		// Parse product JSON from script tag
		try {
			const productJsonElement = this.$refs.productJson
			if (!productJsonElement) {
				console.error('Product JSON element not found')
				return
			}

			const productJson = JSON.parse(productJsonElement.innerHTML || '{}')
			this.variants = productJson.variants || []

			// Check for variant in URL query parameters
			const urlParams = new URLSearchParams(window.location.search)
			const variantFromUrl = urlParams.get('variant')

			if (this.variants.length > 0) {
				let initialVariant

				if (variantFromUrl) {
					// Try to find variant from URL parameter
					initialVariant = this.variants.find((v) => v.id === Number(variantFromUrl))
				}

				// Fall back to default variant if URL variant not found
				if (!initialVariant) {
					const defaultVariantId = productJson.current_variant
					initialVariant = defaultVariantId
						? this.variants.find((v) => v.id === defaultVariantId)
						: this.variants[0]
				}

				this.currentVariant = initialVariant

				// Set initial options based on the selected variant
				if (initialVariant?.options) {
					this.selectedOptions = initialVariant.options.reduce((acc, opt) => {
						acc[opt.name] = opt.value
						return acc
					}, {})
				}
			}

			// Watch for changes to selected options
			this.$watch('selectedOptions', () => {
				this.updateVariant()
			})
		} catch (error) {
			console.error('Error initializing product form:', error)
		}
	},

	animateLines() {
		const horizontalLines = this.$el.querySelectorAll('[data-horizontal-line]')
		const verticalLines = this.$el.querySelectorAll('[data-vertical-line]')

		if (horizontalLines?.length) {
			inView(
				this.$el,
				() =>
					animate(
						horizontalLines,
						{ scaleX: 1 },
						{ duration: 0.4, delay: 0.2, easing: ease.inOutQuint },
					),
				{ amount: 0.075 },
			)
		}
		if (verticalLines?.length) {
			inView(
				this.$el,
				() =>
					animate(
						verticalLines,
						{ scaleY: 1 },
						{ duration: 0.4, delay: 0.2, easing: ease.inOutQuint },
					),
				{ amount: 0.075 },
			)
		}
	},

	increment() {
		this.quantity++
	},

	decrement() {
		if (this.quantity > 1) {
			this.quantity--
		}
	},

	updateOption(optionName, optionValue) {
		this.selectedOptions[optionName] = optionValue
	},

	updateVariant() {
		const selectedOptionsEntries = Object.entries(this.selectedOptions)

		if (selectedOptionsEntries.length === 0) return

		const matchingVariant = this.variants.find((variant) => {
			if (!variant.options) return false

			return selectedOptionsEntries.every(([optionName, optionValue]) => {
				const option = variant.options.find((opt) => opt.name === optionName)
				if (!option) return false

				return option.value === optionValue
			})
		})

		if (matchingVariant) {
			this.currentVariant = matchingVariant

			const idInput = this.$el.querySelector('[name="id"]')
			if (idInput) {
				idInput.value = matchingVariant.id
			}
		} else if (this.debug) {
			console.log('No matching variant found')
		}
	},

	isOptionAvailable(optionName, optionValue) {
		const testSelections = { ...this.selectedOptions }
		testSelections[optionName] = optionValue

		const matchingVariant = this.variants.find((variant) => {
			if (!variant.options) return false

			const matches = Object.entries(testSelections).every(([name, value]) => {
				const option = variant.options.find((opt) => opt.name === name)
				if (!option) return false

				const valueMatch = option.value === value
				return valueMatch
			})

			return matches
		})

		const isAvailable = matchingVariant && matchingVariant.available !== false

		return isAvailable
	},

	getAvailableOptionsFor(optionName) {
		const availableOptions = new Set()

		const currentSelections = { ...this.selectedOptions }
		delete currentSelections[optionName]

		this.variants.forEach((variant) => {
			if (variant.available === false) return

			const matchesCurrentSelections = Object.entries(currentSelections).every(
				([name, value]) => {
					const option = variant.options.find((opt) => opt.name === name)
					if (!option) return false

					return option.value === value
				},
			)

			if (matchesCurrentSelections) {
				const option = variant.options.find((opt) => opt.name === optionName)
				if (option) {
					availableOptions.add(option.value)
				}
			}
		})

		return Array.from(availableOptions)
	},

	get isAvailable() {
		return this.currentVariant?.available !== false
	},

	get currentPrice() {
		return this.currentVariant?.price || ''
	},

	get currentVariantId() {
		return this.currentVariant?.id || ''
	},

	get formattedPrice() {
		let price = this.currentPrice
		if (typeof price === 'string') {
			price = price.replace(/^\$/, '').trim()
		}
		return price
	},

	async submit() {
		if (!this.isAvailable) {
			return
		}

		const form = this.$el
		const formData = new FormData(form)
		const variant = formData.get('id')
		const sellingPlan = formData.get('selling_plan')
		console.log('sellingPlan', sellingPlan)

		const payload = [
			{
				id: Number(variant),
				quantity: 1,
				selling_plan: sellingPlan,
			},
		]

		const cart = this.$store.cart

		await cart.addLines(payload, () => {
			this.$store.site.openCart()
			// window.location.href = '/cart'
		})
	},
})
